import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Collapse, Space, Table, Tag, Typography, message } from "antd";
import { EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import CreatePriceCardElementDialog from "@/features/priceCard/components/CreatePriceCardElementDialog.jsx";
import {
  getPriceCardAudit,
  getManagedPriceCardElements,
} from "@/features/priceCard/services/priceCardElementService.js";

const { Text } = Typography;

/**
 * Ops view of the live card’s typed element contract (PWA / quotes / invoices).
 * @param {{ refreshKey?: number|string }} [props]
 */
export default function LivePriceCardElementsPanel({ refreshKey = 0 }) {
  const [elements, setElements] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingElement, setEditingElement] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [liveElements, auditRows] = await Promise.all([
        getManagedPriceCardElements(),
        getPriceCardAudit(),
      ]);
      setElements(liveElements);
      setAudit(auditRows);
    } catch (error) {
      message.error(error.message || "Live price-card elements could not be loaded");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const columns = [
    { title: "Element", dataIndex: "name", key: "name" },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => <Tag>{String(type || "").replace(/_/g, " ")}</Tag>,
    },
    { title: "Group", dataIndex: "group", key: "group" },
    {
      title: "Customer price",
      key: "price",
      render: (_, row) =>
        Number.isFinite(row.unitPrice)
          ? `${row.pricing?.currency ? `${row.pricing.currency} ` : ""}${row.unitPrice.toFixed(2)}${row.unit ? ` / ${row.unit}` : ""}`
          : "Calculated",
    },
    {
      title: "PWA control",
      dataIndex: "control",
      key: "control",
    },
    {
      title: "Status",
      key: "status",
      render: (_, row) =>
        row.configuredActive === false ? (
          <Tag>Inactive</Tag>
        ) : row.effective === false ? (
          <Tag color="gold">Outside effective dates</Tag>
        ) : (
          <Tag color="green">Active</Tag>
        ),
    },
    { title: "Order", dataIndex: "sortOrder", key: "sortOrder" },
    {
      title: "Effective",
      key: "effective",
      render: (_, row) =>
        [row.effectiveFrom?.slice?.(0, 10), row.effectiveTo?.slice?.(0, 10)]
          .filter(Boolean)
          .join(" → ") || "Always",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => setEditingElement(row)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Card
        title="Live PWA and document elements"
        data-testid="live-price-card-elements-panel"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              Add missing tariff
            </Button>
          </Space>
        }
      >
        <Text type="secondary">
          Live projection of the active named card for the PWA, quotes, and
          standalone invoices. Explicit element edits write back to the live
          card; Shared / Local / Exclusive grids stay the money tables.
        </Text>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={elements}
          loading={loading}
          pagination={{ pageSize: 10 }}
          style={{ marginTop: 16 }}
        />
      </Card>

      <Collapse
        items={[
          {
            key: "audit",
            label: "Recent price-card audit history",
            children: (
              <Table
                rowKey={(row) => row._id || `${row.cardId}-${row.createdAt}`}
                size="small"
                pagination={{ pageSize: 10 }}
                dataSource={audit}
                columns={[
                  { title: "Time", dataIndex: "createdAt", key: "createdAt" },
                  { title: "Action", dataIndex: "action", key: "action" },
                  { title: "Element", dataIndex: "elementId", key: "elementId" },
                  { title: "Actor", dataIndex: "actorId", key: "actorId" },
                  { title: "Reason", dataIndex: "reason", key: "reason" },
                ]}
              />
            ),
          },
        ]}
      />

      <CreatePriceCardElementDialog
        open={createOpen || Boolean(editingElement)}
        initialElement={editingElement}
        onClose={() => {
          setCreateOpen(false);
          setEditingElement(null);
        }}
        onCreated={() => {
          setCreateOpen(false);
          setEditingElement(null);
          load();
        }}
      />
    </Space>
  );
}
